import { httpsCallable } from 'firebase/functions'
import type { ActivityTrackId, SpinTier } from '../types/schema'
import type { EquippableSlotField, ProfileShowcasePatch } from './gamificationService'
import type { ExperienceActionType, ExperienceEventPayload } from '../types/experienceEngine'
import { firebaseFunctions } from './firebase'
import { loadEngineRegistry } from './engineRegistry'
import { areExperienceVisualsEnabled } from './ExperienceEngine'
import { enqueueDeferredEvent } from './deferredEventOutbox'

const entryId = () => crypto.randomUUID().replaceAll('-', '_')

export interface ExperienceSpinResult {
  claimId: string
  baseValue: number
  tier: SpinTier
}

export const executeExperienceSpin = async (tier: SpinTier): Promise<ExperienceSpinResult> => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, ExperienceSpinResult>(firebaseFunctions, 'executeExperienceSpin')
  return (await call({ registryVersion: registry.registryVersion, tier })).data
}

export const claimExperienceSpin = async (claimId: string, doubled: boolean) => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, { creditsAwarded: number }>(firebaseFunctions, 'claimExperienceSpinReward')
  return (await call({ registryVersion: registry.registryVersion, claimId, doubled, celebrationsEnabled: areExperienceVisualsEnabled() })).data
}

export const claimExperienceTrack = async (trackId: ActivityTrackId) => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, { collected: Partial<Record<SpinTier, number>> }>(firebaseFunctions, 'claimExperienceTrackTickets')
  return (await call({ registryVersion: registry.registryVersion, trackId, entryId: entryId() })).data
}

export const restoreExperienceStreak = async (viaAds: boolean) => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable(firebaseFunctions, 'restoreExperienceStreak')
  await call({ registryVersion: registry.registryVersion, viaAds, entryId: entryId() })
}

export const mutateExperienceProfile = async (patch: Partial<Record<EquippableSlotField, string>> | ProfileShowcasePatch) => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable(firebaseFunctions, 'mutateExperienceProfile')
  await call({ registryVersion: registry.registryVersion, patch })
}

export interface ExperienceProcessResult {
  duplicate: boolean
  eventId: string
  celebrationQueued: boolean
  feedbackFormId: string | null
}

/**
 * Synchronous path for events that need server confirmation before the UI
 * updates (e.g. cosmetic unlocks). The background ExperienceEngine still
 * batches passive events; this is for the cases that can't be optimistic.
 */
export const processExperienceEventSync = async (
  action: ExperienceActionType,
  payload: ExperienceEventPayload = {},
): Promise<ExperienceProcessResult> => {
  const registry = await loadEngineRegistry()
  const call = httpsCallable<Record<string, unknown>, ExperienceProcessResult>(firebaseFunctions, 'processExperienceEvent')
  return (await call({
    registryVersion: registry.registryVersion,
    event: {
      eventId: entryId(),
      action,
      workspaceId: payload.workspaceId ?? null,
      sourceId: payload.sourceId ?? payload.subNodeId ?? null,
      occurredAt: new Date().toISOString(),
      payload,
      clientVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
    },
  })).data
}

export const queuePassiveExperienceEvent = async (
  action: ExperienceActionType,
  payload: ExperienceEventPayload = {},
): Promise<boolean> => {
  const registry = await loadEngineRegistry()
  const eventId = entryId()
  return enqueueDeferredEvent({
    clientEventId: eventId,
    actionKey: `EXPERIENCE:${action}`,
    kind: 'EXPERIENCE',
    payload: {
      registryVersion: registry.registryVersion,
      event: {
        eventId,
        action,
        workspaceId: payload.workspaceId ?? null,
        sourceId: payload.sourceId ?? payload.subNodeId ?? null,
        occurredAt: new Date().toISOString(),
        payload,
        clientVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
        celebrationsEnabled: false,
      },
    },
  })
}
